import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { fmtCurrency, fmtNumber } from "@/lib/utils";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

const PIE_COLORS = ["hsl(var(--chart-1))","hsl(var(--chart-2))","hsl(var(--chart-3))","hsl(var(--chart-4))","hsl(var(--chart-5))"];

export default function AnalyticsPage() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  const k = data.kpis;
  const tl = data.timeline.map((d) => ({ date: d.date.slice(5), sent: d.sent, accepted: d.accepted, replied: d.replied, meetings: d.meetings }));
  const pie = Object.entries(data.status_distribution).map(([k2, v]) => ({ name: k2.replace(/_/g, " "), value: v }));

  return (
    <DashboardLayout eyebrow="Analytics" title="Revenue performance">
      <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders bg-card/30">
        <K label="Acceptance" value={`${k.acceptance_rate}%`} sub={`${fmtNumber(k.accepted)} accepted`} />
        <K label="Reply rate" value={`${k.reply_rate}%`} sub={`${fmtNumber(k.replied)} replies`} />
        <K label="Meeting rate" value={`${k.meeting_rate}%`} sub={`${fmtNumber(k.meetings)} meetings`} />
        <K label="Pipeline" value={fmtCurrency(k.pipeline_value)} sub="est. value" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="data-card lg:col-span-2">
          <div className="label-eyebrow mb-1">Volume</div>
          <h3 className="font-display font-semibold tracking-tight text-lg mb-4">14-day daily volume</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tl} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                <Bar dataKey="sent" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} />
                <Bar dataKey="replied" fill="hsl(var(--chart-2))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="data-card">
          <div className="label-eyebrow mb-1">Pipeline</div>
          <h3 className="font-display font-semibold tracking-tight text-lg mb-4">Status distribution</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {pie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function K({ label, value, sub }) {
  return (
    <div className="p-6" data-testid={`analytics-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="text-3xl font-display font-semibold tracking-tightest">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
